'use client';
import { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { usePayOrderMutation } from '@/store/checkout/checkout.api';
import useCallApiProcess from '../useCallApiProcess';
import useOpenPay from '../useOpenPay';
import useStepper from './useStepper';
import {
  useAuth,
  useBayonet,
  useRsvp,
  useEvent,
  useBooking,
  useAddOns,
  useMsci,
  usePayment,
  useCustomLinksActions,
} from '@/store';
import { separateNames } from '@/utils/miscellaneous';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import getSpredlyToken from '../useSpreedly';
import usePaymentGateway from '../usePaymentGateway';
import useAddOnsControl from '@/sections/addons/useAddOnsControl';
import useOrkestaPayCard from './useOrkestaPay';
import useUserTracking from '../userTracking';
import useCheckoutPayment from './useCheckoutPayment';
import useCheckoutValidate3Ds from './useCheckoutValidate3Ds';

export default function useCheckoutPayBooking() {
  useCheckoutValidate3Ds();
  // const [paymentStatus, setPaymentStatus] = useState(null);
  const { t } = useTranslation();
  const router = useRouter();
  const { handleTicketInsuranceHealthy } = useAddOnsControl();

  const { createToken } = useOrkestaPayCard();

  const {
    event: { id, name },
    preferences: { fee_breakdown, currency },
  } = useSelector((state) => state.event);
  const booking = useSelector((state) => state.booking);
  const surveys = useSelector((state) => state.surveys);
  const promotions = useSelector((state) => state.promotions);
  const summary = useSelector((state) => state.summary, shallowEqual);
  const ticketInsurance = useSelector((state) => state.addons.ticketInsurance);

  const { setAttemptsCount, setPaymentStatus, paymentStatus } = usePayment();

  useBooking();

  const {
    customLink: { link },
  } = useCustomLinksActions();

  useEvent();

  const {
    payment: paymentEvent,
    purchase,
    reserveTickets,
  } = useAnalyticsEvents();

  const {
    msci: { selected = null, selectedBin = '' },
  } = useMsci();

  const { addOnsActive } = useAddOns();

  const { method: payment } = useSelector(
    (state) => state.payment,
    shallowEqual,
  );

  const {
    purchase: purchaseTrackingEvent,
    reserveOxxoTickets,
    paymentClicked,
  } = useUserTracking();

  const { nextStep } = useStepper();

  const [payOrderRequest] = usePayOrderMutation();

  const { callApi, alert, showBackdrop, confirmBackCode, closeModal } =
    useCallApiProcess(
      payOrderRequest,
      t('checkout.loadings.processingPayment'),
    );

  const { callApi: callApiOxxo } = useCallApiProcess(
    payOrderRequest,
    t('checkout.loadings.creatingOxxo'),
  );
  const { callApi: callApiFreePay } = useCallApiProcess(
    payOrderRequest,
    t('checkout.loadings.loadingTickets'),
  );

  const { bayonet } = useBayonet();
  const { auth, isGuest } = useAuth();
  const { rsvpStored: rsvp } = useRsvp();

  const { deviceSessionId } = useOpenPay();

  const { getPaymentGateway } = usePaymentGateway();

  const { checkPaymentStatus, verifyPaymentStatusResponse } =
    useCheckoutPayment();

  const sendPuchaseEvent = (oxxo = false) => {
    let ticket_ty = [];

    for (const iterator of booking.orderInfo.tickets) {
      let findType = ticket_ty.find((t) => t === iterator.ticket_type_name);
      if (!findType) ticket_ty.push(iterator.ticket_type_name);
    }

    if (oxxo) {
      reserveTickets({
        event: id,
        currency,
        value: summary.total_to_pay,
      });
    } else {
      purchase({
        event: id,
        currency,
        value: summary.total_to_pay,
        total: summary.total_to_pay,
        ticket_insurance: ticketInsurance.selected,
      });
    }
  };

  const getPaymentName = () => {
    if (payment['Gateway::Banwire'] && payment['Gateway::Banwire'].valid) {
      return separateNames(payment['Gateway::Banwire'].creditCard.name);
    }
    const custom = surveys.custom || surveys.tickets;
    if (custom) {
      const survey = Object.values(custom);
      if (survey.length > 0) {
        if (survey[0].name) {
          return separateNames(survey[0].name);
        }
      }
    }
    const authName = auth?.token?.user_info?.frist_name || auth?.user?.name;
    if (authName) {
      return separateNames(authName);
    }
    return 'invite user';
  };

  const getCustomer = () => {
    const paymentInfo = payment['Gateway::Banwire']
      ? payment['Gateway::Banwire']
      : null;

    if (auth?.token?.role === 'owner' || auth?.token?.role === 'seller') {
      const { customer } = booking;
      return {
        user_id: 'userID',
        name: customer.name,
        last_name: customer.lastName,
        phone_number: `${customer.dialCode}${customer.phone}`,
        email: customer.email,
        zip_code: paymentInfo?.creditCard?.zipCode,
        city: paymentInfo?.creditCard?.city,
        state: paymentInfo?.creditCard?.state,
        country: paymentInfo?.creditCard?.country?.code,
      };
    }

    const { name, lastName } = getPaymentName();

    if (isGuest) {
      return {
        user_id: auth.token ? auth.token.user_info.user_id : 'invite_user',
        name,
        last_name: lastName || name,
        phone_number: auth.user.phone,
        email: auth.user.email,
        zip_code: paymentInfo?.creditCard?.zipCode,
        city: paymentInfo?.creditCard?.city,
        state: paymentInfo?.creditCard?.state,
        country: paymentInfo?.creditCard?.country?.code,
      };
    }

    if (auth.user) {
      return {
        user_id: auth.token ? auth.token.user_info.user_id : '',
        name: auth.token ? auth.token.user_info.frist_name : name,
        last_name:
          auth.token?.user_info.last_name || auth.token?.user_info.frist_name,
        phone_number: auth.token ? auth.token.user_info.phone_number : '',
        email: auth.user.email,
        zip_code: paymentInfo?.creditCard?.zipCode,
        city: paymentInfo?.creditCard?.city,
        state: paymentInfo?.creditCard?.state,
        country: paymentInfo?.creditCard?.country?.code,
      };
    }

    return {
      user_id: 'invite_user',
      name: 'invite',
      last_name: 'invite',
      phone_number: '+525555555555',
      email: 'email@boletia.com',
    };
  };

  const getBaseBody = ({
    method,
    paymentMethodType,
    gateway,
    token,
    deviceID,
    bayonetToken,
    description,
    customerOverride,
  }) => ({
    event_id: id,
    booking_number: booking.order,
    // customerOverride es para los flujos que ya tienen al usuario en la mano
    // y no pueden esperar a que Redux propague el login (TECH-495): getCustomer()
    // lee el store del render anterior y, sin sesión, inventa un comprador.
    customer_request: customerOverride ?? getCustomer(),
    promotion_code: promotions ? promotions.code : null,
    payment_request: {
      payment_method_type: paymentMethodType,
      payment_gateway: gateway,
      fee_breakdown,
      currency,
      method,
      ...(method !== 'oxxo' ? { description: description || '-' } : {}),
      ...(method === 'card'
        ? {
          device_session_id: deviceID,
          source_id: token,
          bayonet_token: bayonetToken,
        }
        : {}),
      ...(rsvp
        ? {
          rsvp: {
            link_id: rsvp.id,
          },
        }
        : {}),
    },
  });

  const paymentCardOrder = async (
    token,
    deviceID,
    payment_gateway = null,
    bayonetToken,
  ) => {
    const body = {
      ...getBaseBody({
        method: 'card',
        paymentMethodType: 'Gateway::Banwire',
        gateway: payment_gateway || 'OPEN_PAY',
        token,
        deviceID,
        bayonetToken,
        description: name,
      }),
      ...(selected
        ? {
          bin: {
            number: String(selectedBin),
            installment: String(selected.payment_plan),
            payment_plan_kind: selected.payment_type,
            selected: true,
          },
        }
        : {}),
      ...(addOnsActive
        ? {
          addons: [
            {
              kind: 'ticket_insurance',
              selected: ticketInsurance.selected,
            },
          ],
        }
        : {}),
    };
    try {
      paymentEvent({
        event: id,
        currency,
        value: summary.total_to_pay,
      });
      paymentClicked({ method: 'card' });
    } catch { }
    return await payOrderRequest({ body, link });
  };

  const paymentOxxoOrder = async () => {
    const body = {
      ...getBaseBody({
        method: 'oxxo',
        paymentMethodType: 'Gateway::Oxxo',
        gateway: 'OXXO_PAY',
      }),
      ...(addOnsActive
        ? {
          addons: [
            {
              kind: 'ticket_insurance',
              selected: ticketInsurance.selected,
            },
          ],
        }
        : {}),
    };
    try {
      paymentEvent({
        event: id,
        currency,
        value: summary.total_to_pay,
      });
      paymentClicked({ method: 'oxxo' });
    } catch { }
    return await callApiOxxo({ body, link });
  };

  const paymentCashOrder = async () => {
    const body = {
      ...getBaseBody({
        method: 'cash',
        paymentMethodType: 'PaymentMethod::Cash',
        gateway: 'CASH_PAY',
      }),
    };

    try {
      paymentEvent({
        event: id,
        currency,
        value: summary.total_to_pay,
      });
      paymentClicked({ method: 'cash' });
    } catch { }
    return await callApi({ body, link });
  };

  const payFreeOrder = async (customerOverride) => {
    const body = {
      ...getBaseBody({
        method: 'cash',
        paymentMethodType: 'PaymentMethod::Cash',
        gateway: 'CASH_PAY',
        customerOverride,
      }),
    };
    try {
      paymentEvent({
        event: id,
        currency,
        value: summary.total_to_pay,
      });
      paymentClicked({ method: 'free' });
    } catch { }
    return callApiFreePay({ body, link });
  };

  const payBooking = async () => {
    if (router.pathname === '/payment') {
      setAttemptsCount();
      try {
        if (summary.total_to_pay === 0) {
          const resp = await payFreeOrder();
          if (!resp.error) {
            sendPuchaseEvent();
            purchaseTrackingEvent({ type: 'free', method: 'none' });
            nextStep();
          }
          return;
        }

        if (payment['Gateway::Banwire']) {
          let payToken, sessionId;
          const payment_gateway = await getPaymentGateway();

          const { bayonetFingerprintToken } = bayonet;

          showBackdrop();

          if (payment_gateway === 'ORKESTA_PAY') {
            const { token, deviceInfo, error } = await createToken();
            if (error) {
              closeModal();
              confirmBackCode(error.code, null);
              return;
            }
            payToken = token.payment_method_id;
            sessionId = deviceInfo.device_session_id;
          } else {
            const response = await getSpredlyToken(
              payment['Gateway::Banwire'].creditCard,
              currency === 'MXN',
            );

            if (!response || response.error) {
              closeModal();
              window.Spreedly.removeHandlers();
              confirmBackCode(t(response.error), null);
              return;
            }

            payToken = response;
            sessionId = deviceSessionId;
          }

          const resp = await paymentCardOrder(
            payToken,
            sessionId,
            payment_gateway,
            bayonetFingerprintToken,
          );
          if (resp.error) {
            if (payment_gateway === 'ADYEN_PAY')
              window.Spreedly.removeHandlers();
            closeModal();
            if (
              resp.error.status === 409 &&
              resp.error.data.code === 'ADDON_NOT_AVAILABLE'
            ) {
              throw resp.error.data.code;
            }
            confirmBackCode(resp.error.data.code, resp.error.data.token);
            return;
          }

          const payment = await checkPaymentStatus(); // status:  completed | notCompleted | need3dsChallenge

          // We implemented the paymentStatus state as a side-effect dependency.
          // It is used to explicitly launch an effect (or function) that relies on
          // the latest state.payment data, guaranteeing it only runs *after* state.payment changes.
          // Without it, the dependent function would execute using state.payment's value
          // from the previous render cycle (stale data).
          setPaymentStatus(payment);
        }

        if (payment['Gateway::Oxxo']) {
          if (summary.total_to_pay > 10000) {
            return alert('No se puede pagar más de 10 mil pesos con Oxxo');
          }
          const resp = await paymentOxxoOrder();

          if (resp?.error) {
            if (
              resp.error.status === 409 &&
              resp.error.data.code === 'ADDON_NOT_AVAILABLE'
            ) {
              throw resp.error.data.code;
            }
            confirmBackCode(resp.error.data.code, resp.error.data.token);
            return;
          }

          sendPuchaseEvent(true);
          reserveOxxoTickets();
          nextStep();
        }
        if (payment['PaymentMethod::Cash']) {
          const resp = await paymentCashOrder();

          if (resp?.error) {
            if (
              resp.error.status === 409 &&
              resp.error.data.code === 'ADDON_NOT_AVAILABLE'
            ) {
              throw resp.error.data.code;
            }
            confirmBackCode(resp.error.data.code, resp.error.data.token);
            return;
          }

          sendPuchaseEvent();
          purchaseTrackingEvent({ type: 'paid', method: 'cash' });
          nextStep();
        }
      } catch (e) {
        closeModal();
        if (e === 'ADDON_NOT_AVAILABLE') {
          handleTicketInsuranceHealthy();
          return;
        }
        return alert(e.error || 'Ocurrió un error al intentar pagar.');
      }
    }
  };

  useEffect(() => {
    if (paymentStatus) {
      verifyPaymentStatusResponse(paymentStatus, 'onPayment');
    }
  }, [paymentStatus]);

  return {
    payBooking,
    payFreeOrder,
  };
}
